import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Download, Loader2, FolderOpen, File,
  FileImage, Upload, Plus, Trash2, X, CheckCircle, Eye, ExternalLink, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  purchase: 'Purchase Documents',
  payment: 'Payment Receipts',
  legal: 'Legal Documents',
  possession: 'Possession Documents',
  builder: 'Builder Documents',
  personal: 'Personal Documents',
  rental: 'Rental Documents',
  other: 'Other'
};

const categoryIcons = {
  purchase: FileText,
  payment: FileText,
  legal: FileText,
  possession: FileText,
  builder: FolderOpen,
  personal: File,
  rental: FileText,
  other: File
};

export default function BuyerDocumentsPage() {
  const [documents, setDocuments] = useState({ buyer_documents: [], builder_documents: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'personal',
    file: null
  });
  
  const fileInputRef = useRef(null);

  const buyerData = JSON.parse(localStorage.getItem('buyerData') || '{}');
  const buyerId = buyerData?.id;

  useEffect(() => {
    if (buyerId) loadDocuments();
  }, [buyerId]);

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/documents?buyer_id=${buyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      }
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error('Only PDF, images, and Word documents are allowed');
        return;
      }
      
      setUploadForm(prev => ({
        ...prev,
        file,
        title: prev.title || file.name.replace(/\.[^/.]+$/, '')
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('buyerToken');
      
      // Simulate file upload progress (in real app, use presigned URL or FormData)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // For demo: Create a data URL as file_url (in production, upload to S3/cloud storage)
      const reader = new FileReader();
      reader.onload = async (e) => {
        clearInterval(progressInterval);
        setUploadProgress(95);
        
        const fileData = {
          title: uploadForm.title,
          description: uploadForm.description,
          category: uploadForm.category,
          file_url: e.target.result, // Base64 data URL for demo
          file_name: uploadForm.file.name,
          file_type: uploadForm.file.name.split('.').pop().toLowerCase(),
          file_size: uploadForm.file.size
        };

        const res = await fetch(`${API}/api/buyer-portal/documents?buyer_id=${buyerId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(fileData)
        });

        if (res.ok) {
          setUploadProgress(100);
          toast.success('Document uploaded successfully!');
          setShowUploadDialog(false);
          setUploadForm({ title: '', description: '', category: 'personal', file: null });
          loadDocuments();
        } else {
          throw new Error('Upload failed');
        }
      };
      
      reader.onerror = () => {
        clearInterval(progressInterval);
        throw new Error('Failed to read file');
      };
      
      reader.readAsDataURL(uploadForm.file);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (docId) => {
    try {
      const token = localStorage.getItem('buyerToken');
      const res = await fetch(`${API}/api/buyer-portal/documents/${docId}?buyer_id=${buyerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Document deleted');
        setDeleteConfirm(null);
        loadDocuments();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const canPreviewInBrowser = (fileType) => {
    const previewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    return previewableTypes.includes(fileType?.toLowerCase());
  };

  const handleView = (doc) => {
    if (canPreviewInBrowser(doc.file_type)) {
      setPreviewDoc(doc);
    } else {
      // For non-previewable types, open in new tab
      window.open(doc.file_url, '_blank');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image') || ['jpg', 'jpeg', 'png'].includes(fileType)) return FileImage;
    return FileText;
  };

  const allDocs = [...documents.buyer_documents, ...documents.builder_documents];
  
  // Filter by category and search query
  const filteredDocs = allDocs.filter(doc => {
    const matchesCategory = activeCategory === 'all' || doc.category === activeCategory;
    const matchesSearch = !searchQuery || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.category?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const categories = ['all', ...new Set(allDocs.map(d => d.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">My Documents</h1>
          <p className="text-muted-foreground">Access all your property documents</p>
        </div>
        <Button 
          onClick={() => setShowUploadDialog(true)}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="upload-document-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search documents by name, description, or category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="document-search-input"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={() => setSearchQuery('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        {categories.map((cat) => (
          <Button
            key={cat}
            variant={activeCategory === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveCategory(cat)}
            className={activeCategory === cat ? 'bg-amber-600' : ''}
          >
            {cat === 'all' ? 'All Documents' : categoryLabels[cat] || cat}
          </Button>
        ))}
      </div>

      {/* Documents */}
      {filteredDocs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium text-lg mb-2">No documents yet</h3>
            <p className="text-muted-foreground">
              {activeCategory === 'all' 
                ? 'Documents will appear here once uploaded by your builder'
                : `No ${categoryLabels[activeCategory] || activeCategory} available`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map((doc) => {
            const FileIcon = getFileIcon(doc.file_type);
            const CategoryIcon = categoryIcons[doc.category] || File;
            const canDelete = doc.uploaded_by === buyerId;
            
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <FileIcon className="h-6 w-6 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{doc.title}</h4>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[doc.category] || doc.category}
                        </Badge>
                        {doc.is_builder_document && (
                          <Badge variant="secondary" className="text-xs">
                            From Builder
                          </Badge>
                        )}
                        {doc.uploaded_by === buyerId && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            My Upload
                          </Badge>
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {doc.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-muted-foreground">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-1">
                          {canDelete && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteConfirm(doc)}
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleView(doc)}
                            data-testid={`view-doc-${doc.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={doc.file_url} download={doc.title}>
                              <Download className="h-4 w-4 mr-1" />
                              Download
                            </a>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Builder Documents Section */}
      {documents.builder_documents.length > 0 && activeCategory === 'all' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-amber-600" />
              Builder Documents
            </CardTitle>
            <CardDescription>Important documents shared by your builder</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3">
              {documents.builder_documents.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">{doc.file_type?.toUpperCase()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleView(doc)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" asChild>
                      <a href={doc.file_url} download={doc.title}>
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-amber-600" />
              Upload Document
            </DialogTitle>
            <DialogDescription>
              Upload your personal documents like ID proofs, bank statements, etc.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Drop Zone */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                ${uploadForm.file ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'}`}
              onClick={() => fileInputRef.current?.click()}
              data-testid="file-drop-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileSelect}
              />
              {uploadForm.file ? (
                <div className="space-y-2">
                  <CheckCircle className="h-10 w-10 mx-auto text-green-500" />
                  <p className="font-medium text-green-700">{uploadForm.file.name}</p>
                  <p className="text-sm text-green-600">{formatFileSize(uploadForm.file.size)}</p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadForm(prev => ({ ...prev, file: null }));
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-10 w-10 mx-auto text-gray-400" />
                  <p className="text-sm text-gray-600">
                    Click to select or drag & drop your file
                  </p>
                  <p className="text-xs text-gray-400">
                    PDF, Images, Word (Max 10MB)
                  </p>
                </div>
              )}
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="doc-title">Document Title *</Label>
              <Input
                id="doc-title"
                placeholder="e.g., Aadhaar Card, Bank Statement"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="doc-title-input"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(val) => setUploadForm(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger data-testid="doc-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal Documents</SelectItem>
                  <SelectItem value="payment">Payment Receipts</SelectItem>
                  <SelectItem value="legal">Legal Documents</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="doc-desc">Description (Optional)</Label>
              <Textarea
                id="doc-desc"
                placeholder="Brief description of the document"
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                data-testid="doc-description-input"
              />
            </div>

            {/* Progress Bar */}
            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={uploading || !uploadForm.file || !uploadForm.title}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Document
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm?.id)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="flex items-center justify-between pr-8">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-600" />
                <span className="truncate">{previewDoc?.title}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {previewDoc?.file_type?.toUpperCase()}
                </Badge>
                <Button variant="outline" size="sm" asChild>
                  <a href={previewDoc?.file_url} download={previewDoc?.title}>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={previewDoc?.file_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open
                  </a>
                </Button>
              </div>
            </DialogTitle>
            {previewDoc?.description && (
              <DialogDescription className="text-sm">
                {previewDoc.description}
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-gray-100" style={{ height: 'calc(90vh - 120px)' }}>
            {previewDoc?.file_type?.toLowerCase() === 'pdf' ? (
              <iframe
                src={previewDoc.file_url}
                className="w-full h-full"
                title={previewDoc.title}
                style={{ minHeight: '600px' }}
              />
            ) : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(previewDoc?.file_type?.toLowerCase()) ? (
              <div className="flex items-center justify-center p-4 h-full">
                <img
                  src={previewDoc.file_url}
                  alt={previewDoc.title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
                <FileText className="h-16 w-16 text-gray-400" />
                <p className="text-gray-600">Preview not available for this file type</p>
                <Button asChild>
                  <a href={previewDoc?.file_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in New Tab
                  </a>
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
